import { useSearchParams } from "react-router-dom";
import type { Appointment } from "@/features/appointments/types/appointmentTypes";
import { useClients } from "@/features/clients/hooks/clientQueries";
import { useMarkNotificationAsRead, useNotifications } from "@/features/notifications/hooks/notificationQueries";
import type { Notification } from "@/features/notifications/types/notificationTypes";
import { useServices } from "@/features/services/hooks/serviceQueries";
import { getServiceName } from "@/features/services/utils/getServiceName";
import { Button, buttonVariants } from "@/shared/components/shadcn/Button";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FullScreenDialog } from "@/shared/components/ui/FullScreenDialog";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Section } from "@/shared/components/ui/Section";
import { SectionAction } from "@/shared/components/ui/SectionAction";
import { StatusBadge } from "@/shared/components/ui/StatusBadge";
import { Tabs } from "@/shared/components/ui/Tabs";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { formatBusinessDate } from "@/shared/utils/formatters";

type AttentionItem =
  | { kind: "pending"; appointment: Appointment; id: string }
  | { kind: "notification"; notification: Notification; id: string };

interface Props {
  appointments: Appointment[];
  businessId: string;
  locationId: string;
  timeZone: string;
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
}

const isAppointmentNotification = (notification: Notification) =>
  ["appointment", "booking"].includes(notification.entityType?.toLowerCase() || "");

const isCancellation = (notification: Notification) =>
  ["appointment_cancelled", "booking_cancelled", "appointment_canceled", "booking_canceled"].includes(notification.type);

export function DashboardAttention({ appointments, businessId, locationId, timeZone, loading, error, onRetry }: Props) {
  const notifications = useNotifications(true);
  const clients = useClients();
  const services = useServices();
  const markAsRead = useMarkNotificationAsRead();
  const { goToAppointmentDetails } = useAppNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const attentionParam = searchParams.get("attention");
  const open = ["all", "pending", "notifications"].includes(attentionParam || "");
  const filter = attentionParam === "pending" || attentionParam === "notifications" ? attentionParam : "all";
  const setFilter = (value: "all" | "pending" | "notifications") => {
    const next = new URLSearchParams(searchParams);
    next.set("attention", value);
    setSearchParams(next, { replace: open });
  };
  const close = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("attention");
    setSearchParams(next, { replace: true });
  };
  const pending = appointments
    .filter((appointment) => appointment.businessId === businessId && appointment.locationId === locationId && appointment.status === "pending")
    .sort((a, b) => (a.startAt?.getTime() ?? Infinity) - (b.startAt?.getTime() ?? Infinity));
  const pendingIds = new Set(pending.map((appointment) => appointment.id));
  const appointmentMap = new Map(appointments.map((appointment) => [appointment.id, appointment]));
  const unread = (notifications.data || []).filter((notification) => {
    if (notification.read || notification.businessId !== businessId) return false;
    if (!isAppointmentNotification(notification)) return true;
    const appointment = notification.entityId ? appointmentMap.get(notification.entityId) : undefined;
    if (appointment && (appointment.businessId !== businessId || appointment.locationId !== locationId)) return false;
    // A pending appointment is the source of truth, not a historical booking-created event.
    return !notification.entityId || !pendingIds.has(notification.entityId) || isCancellation(notification);
  }).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  const items: AttentionItem[] = [
    ...pending.map((appointment): AttentionItem => ({ kind: "pending", appointment, id: `appointment-${appointment.id}` })),
    ...unread.map((notification): AttentionItem => ({ kind: "notification", notification, id: `notification-${notification.id}` })),
  ];
  const filtered = items.filter((item) => filter === "all" || (filter === "pending" ? item.kind === "pending" : item.kind === "notification"));
  const fetchError = error || notifications.error || clients.error || services.error;
  const busy = loading || notifications.isLoading || clients.isLoading || services.isLoading;
  const retry = () => { onRetry(); void notifications.refetch(); void clients.refetch(); void services.refetch(); };

  const renderItem = (item: AttentionItem) => {
    if (item.kind === "pending") {
      const { appointment } = item;
      const client = clients.data?.find((customer) => customer.id === appointment.clientId && customer.businessId === businessId);
      return (
        <InfoRow
          key={item.id}
          title={client ? `${client.firstName} ${client.lastName}`.trim() : appointment.clientId ? "Customer unavailable" : "No customer assigned"}
          topCaption={<StatusBadge tone="pending">Pending booking</StatusBadge>}
          subtitle={getServiceName(appointment, services.data) || "No service assigned"}
          caption={appointment.startAt ? formatBusinessDate(appointment.startAt, timeZone, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Time not set"}
          trailing={<span className={buttonVariants({ variant: "outline", size: "sm" })}>Review</span>}
          onClick={() => goToAppointmentDetails(appointment.id, false)}
          className="border-l-4 border-l-primary"
        />
      );
    }
    const { notification } = item;
    const appointment = notification.entityId && isAppointmentNotification(notification) ? appointmentMap.get(notification.entityId) : undefined;
    const cancellation = isCancellation(notification);
    return (
      <InfoRow
        key={item.id}
        title={notification.title || (cancellation ? "Cancellation receipt" : "Notification")}
        topCaption={<StatusBadge>{cancellation ? "Cancellation" : "Update"}</StatusBadge>}
        caption={<>
          <span className="block">{notification.message}</span>
          <span className="block">{formatBusinessDate(notification.createdAt, timeZone, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}{!appointment && " · Business-wide notification"}</span>
        </>}
        actions={<Button variant="quiet" size="sm" disabled={markAsRead.isPending} onClick={() => markAsRead.mutate(notification.id)}>
          {markAsRead.isPending && markAsRead.variables === notification.id ? "Saving…" : "Mark as read"}
        </Button>}
        className="border-l-4 border-l-primary"
      />
    );
  };

  const renderList = (list: AttentionItem[]) => (
    <>
      {markAsRead.error && <ErrorState message={`Could not mark notification as read: ${markAsRead.error.message}`} />}
      {fetchError ? <ErrorState message={fetchError.message} onRetry={retry} />
        : busy ? <RowSkeleton />
        : list.length ? <div className="grid gap-2.5">{list.map(renderItem)}</div>
        : <EmptyState message={filter === "all" || !open ? "All caught up. Nothing needs your attention." : "No items in this filter."} />}
    </>
  );

  return (
    <>
      <Section
        title="Needs your attention"
        count={busy ? "Loading items…" : fetchError ? "Items unavailable" : `${items.length} ${items.length === 1 ? "item" : "items"}`}
        action={<SectionAction aria-label="View all items needing attention" onClick={() => setFilter("all")}>View all</SectionAction>}
      >
        {renderList(items.slice(0, 3))}
      </Section>
      <FullScreenDialog open={open} onClose={close} title="Needs your attention" description="Review pending bookings before confirming. Marking a cancellation receipt as read never changes the appointment. Notifications without a known location apply to the whole business.">
        <div className="space-y-4 px-4 pb-6 sm:px-[30px]">
          <Tabs label="Attention filter" value={filter} onValueChange={setFilter} options={[
            { value: "all", label: "All" },
            { value: "pending", label: "Pending" },
            { value: "notifications", label: "Updates" },
          ]} />
          <p className="text-xs text-muted-foreground">{filtered.length} of {items.length} items</p>
          {renderList(filtered)}
        </div>
      </FullScreenDialog>
    </>
  );
}
